import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { LocationInfoComponent } from "../../components/location-info/location-info.component";
import { CommonModule } from '@angular/common';
import { Location, LocationConsumptionData } from "../../../../core/interfaces/location";
import { AddNewLocationStepOneDialogComponent } from "../../components/dialogs/add-new-location-step-one-dialog/add-new-location-step-one-dialog.component";
import { AddNewLocationDialogComponent } from '../../components/dialogs/add-new-location-dialog/add-new-location-dialog.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocationService } from '../../../../core/api/location.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { DeleteLocationDialogComponent } from "../../components/dialogs/delete-location-dialog/delete-location-dialog.component";
import { ConfirmLocationsDialogComponent } from "../../components/dialogs/confirm-locations-dialog/confirm-locations-dialog.component";
import { ElectricityInvoice, GasInvoice } from '../../../../core/interfaces/invoice-interfaces';

@Component({
  selector: 'app-locations-overview-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, TabButtonComponent, CustomButtonComponent, LocationInfoComponent, CommonModule, AddNewLocationStepOneDialogComponent, AddNewLocationDialogComponent, TranslocoPipe, ToastComponent, CustomFooterComponent, DeleteLocationDialogComponent, ConfirmLocationsDialogComponent],
  templateUrl: './locations-overview-page.component.html',
  styleUrl: './locations-overview-page.component.css'
})
export class LocationsOverviewPageComponent implements OnInit {
  selectedSidebarItemId: number = 4;

  // Add new location upload step one dialog
  @ViewChild(AddNewLocationStepOneDialogComponent) newLocationUploadDialog!: AddNewLocationStepOneDialogComponent;

  // Add location data dialog
  @ViewChild(AddNewLocationDialogComponent) addLocationDataDialog!: AddNewLocationDialogComponent;

  // Delete location data dialog
  @ViewChild(DeleteLocationDialogComponent) deleteLocationDialog!: DeleteLocationDialogComponent;

  // Confirm inactive locations dialog
  @ViewChild(ConfirmLocationsDialogComponent) confirmLocationsDialog!: ConfirmLocationsDialogComponent;

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  locations: Location[] = [];
  locationsLoaded: boolean = false;

  locationsConsumption: LocationConsumptionData[] = [];
  locationDataMap: { [locationId: string]: LocationConsumptionData } = {};

  locationElectricityDataMap: { [locationId: string]: ElectricityInvoice } = {};
  locationGasDataMap: { [locationId: string]: GasInvoice } = {};

  inactiveLocations: Location[] = [];
  inactiveLocationsShown = false;

  constructor(private locationService: LocationService, private apiError: ApiErrorService, private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    // Get user locations
    this.loadLocations();

    this.cdRef.detectChanges();
  }

  openNewLocationUploadDialog(): void {
    this.newLocationUploadDialog.openModal();
  }

  openAddLocationDataDialog = () => {
    this.addLocationDataDialog.openModal();
  }

  loadLocations() {
    this.locationService.getLocations().subscribe({
      next: (locations) => {
        // If locations exists show them
        if (locations) {
          this.locations = locations;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.locationsLoaded = false;
      },
      complete: () => {
        // Mark api loading as completed
        this.locationsLoaded = true;

        this.inactiveLocations = this.locations.filter(loc => !loc.is_active);

        // TODO: check for number of included locations from users membership (1 for all for now)
        if (this.inactiveLocations.length > 0 && !this.inactiveLocationsShown) {
          this.inactiveLocationsShown = true;
          this.confirmLocationsDialog.openModal(((this.locations.length - this.inactiveLocations.length) == 0));
        }

        this.loadLocationsConsumption();
        this.loadElectricityLatestInvoices();
        this.loadGasLatestInvoices();
      }
    });
  }

  loadLocationsConsumption() {
    this.locationService.getLocationsConsumption().subscribe({
      next: (locations) => {
        if (locations) {
          this.locationsConsumption = locations;

          this.locationDataMap = {};
          for (const loc of locations) {
            this.locationDataMap[loc.location_id] = loc;
          }
        }
      },
      error: async (error) => {
        // Pass
      },
      complete: () => {
      }
    });
  }

  loadElectricityLatestInvoices() {
    this.locationService.getLocationLatestElectricityInvoices().subscribe({
      next: (locations) => {
        if (locations) {
          this.locationsConsumption = locations;

          this.locationElectricityDataMap = {};
          for (const loc of locations) {
            this.locationElectricityDataMap[loc.location_id] = loc;
          }
        }
      },
      error: async (error) => {
        // Pass
      },
      complete: () => {
      }
    });
  }

  loadGasLatestInvoices() {
    this.locationService.getLocationLatestGasInvoices().subscribe({
      next: (locations) => {
        if (locations) {
          this.locationsConsumption = locations;

          this.locationGasDataMap = {};
          for (const loc of locations) {
            this.locationGasDataMap[loc.location_id] = loc;
          }
        }
      },
      error: async (error) => {
        // Pass
      },
      complete: () => {
      }
    });
  }

  loadLocationsCallback = () => this.loadLocations();

  editLocation = (location: Location) => {
    this.addLocationDataDialog.openModal(location, this.locationElectricityDataMap[location.id], this.locationGasDataMap[location.id]);
  }

  deleteCallbackFunction = (location: Location) => {
    this.deleteLocationDialog.openModal(location);
  }
}
